import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { getApiService } from '../../../shared/services/api/api.service';
import { Payment, PaymentStatus } from '../types/payment.types';

export const usePayments = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getApiService().getPayments();

      if (response.success) {
        // Sort by created_at descending (most recent first)
        const sortedPayments = [...response.data].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        });
        setPayments(sortedPayments);
        setFilteredPayments(sortedPayments);
      } else {
        throw new Error('Failed to fetch payments');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch payments';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Apply filters whenever payments, statusFilter, or searchQuery changes
  useEffect(() => {
    let result = [...payments];

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(payment => payment.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(payment =>
        payment.transaction_id.toLowerCase().includes(query) ||
        payment.account?.toLowerCase().includes(query) ||
        payment.phone?.toLowerCase().includes(query)
      );
    }

    setFilteredPayments(result);
  }, [payments, statusFilter, searchQuery]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const refresh = useCallback(() => {
    fetchPayments();
  }, [fetchPayments]);

  const filterByStatus = useCallback((status: PaymentStatus | 'all') => {
    setStatusFilter(status);
  }, []);

  const search = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    payments: filteredPayments,
    isLoading,
    error,
    refresh,
    filterByStatus,
    search,
    statusFilter,
    searchQuery,
  };
};
